import React, { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SitesSearch from './component'
import { RootState, Site, SiteFilters } from 'types'
import { fetchSites, clearSites } from 'store/sitesManager'
import { selectSites, selectSitesIsLoading } from 'store/sitesManager/selector'

type SiteSearchCriteria = Partial<SiteFilters>

const SitesSearchContainer: React.FC = () => {
  const dispatch = useDispatch()
  const [criteria, setCriteria] = useState<SiteSearchCriteria>({})

  const sites = useSelector<RootState, Site[] | null>(
    state => selectSites(state.sitesManager) || null,
  )
  const isLoading = useSelector<RootState, boolean>(state =>
    selectSitesIsLoading(state.sitesManager),
  )

  const updateCriteria = useCallback(
    (key: keyof SiteSearchCriteria, value: string | number) => {
      setCriteria(prev => {
        if (!value) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [key]: _, ...rest } = prev
          return rest
        }
        return { ...prev, [key]: value }
      })
    },
    [],
  )

  const clearSearch = useCallback(() => {
    setCriteria({})
    dispatch(clearSites())
  }, [dispatch])

  const search = useCallback(() => {
    dispatch(fetchSites(criteria as SiteFilters))
  }, [dispatch, criteria])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      const numValue = value ? parseInt(value, 10) : ''
      updateCriteria(name as keyof SiteSearchCriteria, numValue)
    },
    [updateCriteria],
  )

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') search()
    },
    [search],
  )

  return (
    <SitesSearch
      clearSearch={clearSearch}
      criteria={criteria}
      handleChange={handleChange}
      handleKeydown={handleKeydown}
      isLoading={isLoading}
      search={search}
      sites={sites}
    />
  )
}

export default SitesSearchContainer
